import { useState, useMemo, useEffect } from "react";
import { Calculator, Download, Send, Save } from "lucide-react";
import { useDataProvider, useNotify } from "ra-core";
import { openWhatsAppWeb } from "@/lib/whatsapp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { Property } from "@/types/realEstate";
import {
  downloadSimplePdf,
  formatPriceInINR,
  formatPriceInINRAscii,
  type PdfLine,
} from "@/lib/formatters";
import { useConfigurationContext } from "../root/ConfigurationContext";
import { calculateCostSheet } from "./costSheetUtils";

interface CostSheetCalculatorModalProps {
  property: Property | null;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Buyer phone for WhatsApp quote; falls back to skip if empty */
  contactPhone?: string;
}

export const CostSheetCalculatorModal = ({
  property,
  isOpen,
  onOpenChange,
  contactPhone,
}: CostSheetCalculatorModalProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const { title: crmTitle } = useConfigurationContext();
  const brandTitle = crmTitle?.trim() || "Blivix CRM";

  const area = property?.carpet_area_sqft || 1500;
  const initialBaseRate = property
    ? Math.round(property.base_price / Math.max(area, 1))
    : 8500;

  const [baseRateSqft, setBaseRateSqft] = useState(initialBaseRate.toString());
  const [plcCharges, setPlcCharges] = useState("250000");
  const [parkingCharges, setParkingCharges] = useState("300000");
  const [clubCharges, setClubCharges] = useState("150000");
  const [gstPercent, setGstPercent] = useState("5.0");
  const [stampDutyPercent, setStampDutyPercent] = useState("6.0");
  const [registrationFee] = useState("30000");
  const [saving, setSaving] = useState(false);

  // Reset inputs when property changes or modal re-opens for a new unit
  useEffect(() => {
    if (!isOpen || !property) return;
    const nextArea = property.carpet_area_sqft || 1500;
    const nextRate = Math.round(property.base_price / Math.max(nextArea, 1));
    setBaseRateSqft(String(nextRate));
    setPlcCharges("250000");
    setParkingCharges("300000");
    setClubCharges("150000");
    setGstPercent("5.0");
    setStampDutyPercent("6.0");
  }, [isOpen, property]);

  const calc = useMemo(
    () =>
      calculateCostSheet({
        areaSqft: area,
        baseRateSqft: Number(baseRateSqft),
        plcCharges: Number(plcCharges),
        parkingCharges: Number(parkingCharges),
        clubCharges: Number(clubCharges),
        gstPercent: Number(gstPercent),
        stampDutyPercent: Number(stampDutyPercent),
        registrationFee: Number(registrationFee),
      }),
    [
      area,
      baseRateSqft,
      plcCharges,
      parkingCharges,
      clubCharges,
      gstPercent,
      stampDutyPercent,
      registrationFee,
    ],
  );

  const handleDownloadPdf = () => {
    if (!property) return;
    const lines: PdfLine[] = [
      { kind: "title", text: brandTitle },
      { kind: "subtitle", text: "COST SHEET QUOTE" },
      { kind: "rule" },
      {
        kind: "muted",
        text: `Date: ${new Date().toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "long",
          year: "numeric",
        })}`,
      },
      { kind: "spacer" },
      { kind: "heading", text: "Unit & Project" },
      { kind: "body", text: `Unit: ${property.unit_number}` },
      { kind: "body", text: `Project: ${property.project?.name || "-"}` },
      {
        kind: "body",
        text: `Configuration: ${property.configuration || "-"}`,
      },
      {
        kind: "body",
        text: `Carpet area: ${property.carpet_area_sqft || area} sqft`,
      },
      { kind: "spacer" },
      { kind: "heading", text: "Cost Breakdown" },
      {
        kind: "body",
        text: `Base rate / sqft: Rs. ${Number(baseRateSqft).toLocaleString("en-IN")}`,
      },
      {
        kind: "body",
        text: `Basic cost: ${formatPriceInINRAscii(calc.basicCost)}`,
      },
      { kind: "body", text: `PLC: ${formatPriceInINRAscii(calc.totalPLC)}` },
      {
        kind: "body",
        text: `Parking: ${formatPriceInINRAscii(calc.parking)}`,
      },
      { kind: "body", text: `Club: ${formatPriceInINRAscii(calc.club)}` },
      {
        kind: "body",
        text: `Net agreement: ${formatPriceInINRAscii(calc.netAgreementValue)}`,
      },
      { kind: "body", text: `GST: ${formatPriceInINRAscii(calc.gstAmount)}` },
      {
        kind: "body",
        text: `Stamp duty: ${formatPriceInINRAscii(calc.stampDutyAmount)}`,
      },
      {
        kind: "body",
        text: `Registration: ${formatPriceInINRAscii(calc.reg)}`,
      },
      { kind: "spacer" },
      {
        kind: "subtitle",
        text: `GRAND TOTAL: ${formatPriceInINRAscii(calc.grandTotal)}`,
      },
      { kind: "rule" },
      {
        kind: "muted",
        text: `Valid for 7 days. Generated from ${brandTitle}.`,
      },
    ];
    downloadSimplePdf(
      `cost-sheet-${property.unit_number}`.replace(/[^\w.-]+/g, "_"),
      lines,
      { brandTitle },
    );
    notify("Cost sheet PDF downloaded", { type: "info" });
  };

  const handleSaveCostSheet = async () => {
    if (!property?.id) {
      notify("No property selected", { type: "warning" });
      return;
    }
    setSaving(true);
    try {
      await dataProvider.create("cost_sheets", {
        data: {
          property_id: property.id,
          base_rate_sqft: Number(baseRateSqft) || 0,
          plc_charges: Number(plcCharges) || 0,
          car_parking_charges: Number(parkingCharges) || 0,
          club_membership_charges: Number(clubCharges) || 0,
          gst_percentage: Number(gstPercent) || 0,
          stamp_duty_percentage: Number(stampDutyPercent) || 0,
          registration_charges: Number(registrationFee) || 0,
          total_agreement_value: calc.grandTotal,
        },
      });
      notify("Cost sheet saved to database", { type: "success" });
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : "Failed to save cost sheet";
      notify(message, { type: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleWhatsAppQuote = () => {
    const phone = (contactPhone || "").trim();
    if (!phone) {
      notify(
        "No buyer phone on this quote — open from a matched contact or enter phone first",
        {
          type: "warning",
        },
      );
      return;
    }
    const msg = `Namaste! Here is the Official Cost Sheet for ${property?.unit_number} (${property?.project?.name}): Net Agreement Value: ${formatPriceInINR(calc.netAgreementValue)}, All-Inclusive Grand Total: ${formatPriceInINR(calc.grandTotal)}. Valid for 7 days.`;
    openWhatsAppWeb(phone, msg);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto print:max-w-none print:shadow-none">
        <DialogHeader className="border-b pb-3">
          <DialogTitle className="flex items-center gap-2 text-xl font-bold">
            <Calculator className="w-6 h-6 text-primary" />
            Cost Sheet & Pricing Breakdown: {property?.unit_number}
          </DialogTitle>
          <DialogDescription>
            {property?.project?.name} - {property?.configuration} (
            {property?.carpet_area_sqft} SqFt)
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4 text-sm">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 bg-muted/40 p-4 rounded-xl border print:hidden">
            <div className="space-y-1">
              <label className="text-xs font-semibold">
                Base Rate / SqFt (₹)
              </label>
              <Input
                type="number"
                value={baseRateSqft}
                onChange={(e) => setBaseRateSqft(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold">PLC Charges (₹)</label>
              <Input
                type="number"
                value={plcCharges}
                onChange={(e) => setPlcCharges(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold">
                Covered Car Parking (₹)
              </label>
              <Input
                type="number"
                value={parkingCharges}
                onChange={(e) => setParkingCharges(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold">Clubhouse Fee (₹)</label>
              <Input
                type="number"
                value={clubCharges}
                onChange={(e) => setClubCharges(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold">GST Rate (%)</label>
              <Input
                type="number"
                step="0.1"
                value={gstPercent}
                onChange={(e) => setGstPercent(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <label className="text-xs font-semibold">Stamp Duty (%)</label>
              <Input
                type="number"
                step="0.1"
                value={stampDutyPercent}
                onChange={(e) => setStampDutyPercent(e.target.value)}
              />
            </div>
          </div>

          <div className="bg-card p-5 rounded-xl border space-y-4 print:border-none print:p-0">
            <div className="flex justify-between items-center border-b pb-3">
              <div>
                <h3 className="font-bold text-lg text-foreground">
                  {property?.unit_number} Official Cost Quotation
                </h3>
                <p className="text-xs text-muted-foreground">
                  {property?.project?.name} | {property?.facing || "East"}{" "}
                  Facing |{" "}
                  {property?.floor_number
                    ? `${property.floor_number}th Floor`
                    : "Ground Floor"}
                </p>
              </div>
              <Badge className="bg-emerald-600 text-white">
                Valid for 7 Days
              </Badge>
            </div>

            <div className="space-y-2 text-xs">
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span>
                  Base Unit Cost ({area} SqFt × ₹
                  {Number(baseRateSqft).toLocaleString("en-IN")}/SqFt):
                </span>
                <span className="font-semibold text-foreground">
                  {formatPriceInINR(calc.basicCost)}
                </span>
              </div>
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span>Preferred Location Charges (PLC):</span>
                <span className="font-semibold text-foreground">
                  {formatPriceInINR(calc.totalPLC)}
                </span>
              </div>
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span>Covered Car Parking Allotment:</span>
                <span className="font-semibold text-foreground">
                  {formatPriceInINR(calc.parking)}
                </span>
              </div>
              <div className="flex justify-between p-2 rounded bg-muted/30">
                <span>Clubhouse & Infrastructure Fee:</span>
                <span className="font-semibold text-foreground">
                  {formatPriceInINR(calc.club)}
                </span>
              </div>

              <div className="flex justify-between p-2.5 rounded bg-primary/10 border border-primary/20 font-bold text-sm text-primary my-2">
                <span>Net Agreement Value:</span>
                <span>{formatPriceInINR(calc.netAgreementValue)}</span>
              </div>

              <div className="pt-2 border-t space-y-1.5">
                <span className="font-semibold text-muted-foreground uppercase text-[10px]">
                  Statutory Government Taxes
                </span>
                <div className="flex justify-between p-2 rounded bg-muted/30">
                  <span>GST ({gstPercent}%):</span>
                  <span className="font-semibold text-foreground">
                    {formatPriceInINR(calc.gstAmount)}
                  </span>
                </div>
                <div className="flex justify-between p-2 rounded bg-muted/30">
                  <span>Stamp Duty ({stampDutyPercent}%):</span>
                  <span className="font-semibold text-foreground">
                    {formatPriceInINR(calc.stampDutyAmount)}
                  </span>
                </div>
                <div className="flex justify-between p-2 rounded bg-muted/30">
                  <span>Registration & Legal Documentation:</span>
                  <span className="font-semibold text-foreground">
                    {formatPriceInINR(calc.reg)}
                  </span>
                </div>
              </div>

              <div className="flex justify-between p-3.5 rounded-xl bg-primary text-primary-foreground font-extrabold text-base mt-4 shadow-md">
                <span>All-Inclusive Total Price:</span>
                <span>{formatPriceInINR(calc.grandTotal)}</span>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter className="print:hidden flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
          <Button
            variant="outline"
            onClick={handleSaveCostSheet}
            disabled={saving}
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? "Saving…" : "Save to DB"}
          </Button>
          <Button
            variant="outline"
            className="border-emerald-600 text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-950/30"
            onClick={handleWhatsAppQuote}
          >
            <Send className="w-4 h-4 mr-2" /> Send Quote on WhatsApp
          </Button>
          <Button
            onClick={handleDownloadPdf}
            className="bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            <Download className="w-4 h-4 mr-2" /> Download PDF Quote
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CostSheetCalculatorModal;
